import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { 
  Avatar, Dialog, DialogContent, Divider, Grid, Typography, 
  Chip, Box, Card, CardContent, IconButton, Table, TableBody, 
  TableCell, TableContainer, TableHead, TableRow, Paper,
  Button
} from '@mui/material';
import { Visibility, Close, LocalShipping, CheckCircle, Cancel, Pending, Phone, Email, LocationOn, ShoppingBag, CalendarToday, CurrencyRupee, Print } from '@mui/icons-material';
import logo from '../../../assets/images/Logo/Bharat-Darshan-Logo.png';
import { Color } from '../../../assets/colors/index.js';
import { api_urls } from '../../../utils/api-urls/index.js';
import { IndianRupee } from '../../../utils/common-function';
import { ViewSvg, CrossSvg } from '../../../assets/svg/index.js';
import MainDatatable from '../../../components/common/MainDatatable.jsx';
import * as AstromallActions from '../../../redux/actions/astromallAction.js';

const OrderHistory = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const orderHistoryData = useSelector(state => state.astromallReducer.orderHistoryData);

  const [detailsModal, setDetailsModal] = useState({ isOpen: false, order: null });
  const [productModal, setProductModal] = useState({ isOpen: false, productData: [] });

  // Stats calculation
  const stats = {
    total: orderHistoryData?.length || 0,
    initiated: orderHistoryData?.filter(o => o.status === 'INITIATED')?.length || 0,
    accepted: orderHistoryData?.filter(o => o.status === 'ACCEPTED')?.length || 0,
    delivered: orderHistoryData?.filter(o => o.status === 'DELIVERED')?.length || 0,
    cancelled: orderHistoryData?.filter(o => o.status === 'CANCELLED' || o.status === 'REJECTED')?.length || 0,
    totalRevenue: orderHistoryData?.reduce((sum, o) => sum + (o.amount || 0), 0) || 0,
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'DELIVERED': return 'success';
      case 'ACCEPTED': case 'PACKED': case 'OUT_FOR_DELIVERY': return 'primary';
      case 'INITIATED': return 'warning';
      case 'CANCELLED': case 'REJECTED': return 'error';
      default: return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'DELIVERED': return <CheckCircle fontSize="small" />;
      case 'ACCEPTED': case 'PACKED': case 'OUT_FOR_DELIVERY': return <LocalShipping fontSize="small" />;
      case 'INITIATED': return <Pending fontSize="small" />;
      case 'CANCELLED': case 'REJECTED': return <Cancel fontSize="small" />;
      default: return null;
    }
  };

  //* Order History DataTable Columns
  const orderHistoryColumns = [
    { name: 'S.No.', selector: row => orderHistoryData.indexOf(row) + 1, width: '60px' },
    { 
      name: 'Order ID', 
      selector: row => row?.invoiceId || row?._id?.slice(-8),
      width: '120px'
    },
    { 
      name: 'Customer', 
      cell: row => (
        <Box>
          <Typography variant="body2" fontWeight="bold">
            {row?.customerName || row?.addressId?.email?.split('@')[0] || 'Customer'}
          </Typography>
          <Typography variant="caption" color="textSecondary">
            {row?.addressId?.phone || '-'}
          </Typography>
        </Box>
      ),
      width: '150px'
    },
    { 
      name: 'Address',
      cell: row => (
        <Box sx={{ maxWidth: 150, overflow: 'hidden' }}>
          <Typography variant="caption" noWrap title={row?.addressId?.address}>
            {row?.addressId?.address || '-'}
          </Typography>
          <Typography variant="caption" display="block" color="textSecondary">
            {row?.addressId?.city}, {row?.addressId?.state}
          </Typography>
        </Box>
      ),
      width: '150px'
    },
    { 
      name: 'Total Paid', 
      cell: row => {
        const productAmount = row?.amount || 0;
        const shipping = row?.shippingCharges || (productAmount < 1000 ? 90 : 0);
        const totalPaid = row?.totalPaid || (productAmount + shipping);
        return (
          <Box>
            <Typography fontWeight="bold" color="primary">
              {IndianRupee(totalPaid)}
            </Typography>
            {shipping > 0 && (
              <Typography variant="caption" color="textSecondary">
                (incl. {IndianRupee(shipping)} shipping)
              </Typography>
            )}
          </Box>
        );
      },
      width: '130px'
    },
    { 
      name: 'Items', 
      selector: row => row?.products?.length || 0,
      width: '80px'
    },
    { 
      name: 'Status', 
      cell: row => (
        <Chip 
          icon={getStatusIcon(row?.status)}
          label={row?.status?.replace(/_/g, ' ')} 
          color={getStatusColor(row?.status)}
          size="small"
        />
      ),
      width: '150px'
    },
    {
      name: "Change Status",
      cell: (row) => (
        <select 
          value={row?.status} 
          onChange={(e) => dispatch(AstromallActions.changeOrderStatus({ orderId: row?._id, status: e.target.value }))} 
          style={{ outline: "none", padding: "5px 8px", border: "1px solid #666666", color: "#666666", borderRadius: "5px", fontFamily: "Philosopher", fontSize: '12px' }}
        >
          <option value="">---Select---</option>
          <option value={'INITIATED'}>Initiated</option>
          <option value={'ACCEPTED'}>Accepted</option>
          <option value={'PACKED'}>Packed</option>
          <option value={'REJECTED'}>Rejected</option>
          <option value={'OUT_FOR_DELIVERY'}>Out for delivery</option>
          <option value={'DELIVERED'}>Delivered</option>
          <option value={'CANCELLED'}>Cancelled</option>
        </select>
      ), 
      width: '140px'
    },
    { 
      name: 'Date', 
      cell: row => (
        <Box>
          <Typography variant="caption">
            {row?.createdAt ? moment(row?.createdAt).format('DD MMM YYYY') : 'N/A'}
          </Typography>
          <Typography variant="caption" display="block" color="textSecondary">
            {row?.createdAt ? moment(row?.createdAt).format('hh:mm A') : ''}
          </Typography>
        </Box>
      ),
      width: '110px'
    },
    {
      name: 'Actions',
      cell: row => (
        <Box sx={{ display: "flex", gap: 1 }}>
          <IconButton 
            size="small" 
            color="primary"
            onClick={() => setDetailsModal({ isOpen: true, order: row })}
            title="View Details"
          >
            <Visibility fontSize="small" />
          </IconButton>
          <IconButton 
            size="small" 
            color="secondary"
            onClick={() => setProductModal({ isOpen: true, productData: row?.products })}
            title="View Products"
          >
            <ShoppingBag fontSize="small" />
          </IconButton>
        </Box>
      ),
      width: '100px'
    },
  ];

  useEffect(() => {
    //! Dispatching API for Getting Order History
    dispatch(AstromallActions.getOrderHistory());
  }, [dispatch]);

  return (
    <Box sx={{ p: 2 }}>
      {/* Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={4} md={2}>
          <Card sx={{ bgcolor: '#e3f2fd' }}>
            <CardContent sx={{ py: 1, textAlign: 'center' }}>
              <Typography variant="h5" fontWeight="bold">{stats.total}</Typography>
              <Typography variant="caption">Total Orders</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <Card sx={{ bgcolor: '#fff3e0' }}>
            <CardContent sx={{ py: 1, textAlign: 'center' }}>
              <Typography variant="h5" fontWeight="bold" color="warning.main">{stats.initiated}</Typography>
              <Typography variant="caption">Pending</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <Card sx={{ bgcolor: '#e3f2fd' }}>
            <CardContent sx={{ py: 1, textAlign: 'center' }}>
              <Typography variant="h5" fontWeight="bold" color="primary.main">{stats.accepted}</Typography>
              <Typography variant="caption">Processing</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <Card sx={{ bgcolor: '#e8f5e9' }}>
            <CardContent sx={{ py: 1, textAlign: 'center' }}>
              <Typography variant="h5" fontWeight="bold" color="success.main">{stats.delivered}</Typography>
              <Typography variant="caption">Delivered</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <Card sx={{ bgcolor: '#ffebee' }}>
            <CardContent sx={{ py: 1, textAlign: 'center' }}>
              <Typography variant="h5" fontWeight="bold" color="error.main">{stats.cancelled}</Typography>
              <Typography variant="caption">Cancelled</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <Card sx={{ bgcolor: '#f3e5f5' }}>
            <CardContent sx={{ py: 1, textAlign: 'center' }}>
              <Typography variant="h5" fontWeight="bold" color="secondary.main">{IndianRupee(stats.totalRevenue)}</Typography>
              <Typography variant="caption">Revenue</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Data Table */}
      {orderHistoryData && <MainDatatable data={orderHistoryData} columns={orderHistoryColumns} title={'Astro Mall Orders'} />}

      {/* Order Details Modal */}
      <Dialog 
        open={detailsModal?.isOpen} 
        onClose={() => setDetailsModal({ isOpen: false, order: null })}
        maxWidth="md"
        fullWidth
      >
        <Box sx={{ bgcolor: '#1976d2', color: 'white', p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" fontWeight="bold">
            <ShoppingBag sx={{ mr: 1, verticalAlign: 'bottom' }} />
            Order Details - {detailsModal?.order?.invoiceId || '#' + detailsModal?.order?._id?.slice(-8)}
          </Typography>
          <IconButton onClick={() => setDetailsModal({ isOpen: false, order: null })} sx={{ color: 'white' }}>
            <Close />
          </IconButton>
        </Box>
        <DialogContent dividers>
          {detailsModal?.order && (
            <Grid container spacing={3}>
              {/* Order Info */}
              <Grid item xs={12} md={6}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle1" fontWeight="bold" gutterBottom color="primary">
                      <CalendarToday sx={{ fontSize: 18, mr: 1, verticalAlign: 'text-bottom' }} />
                      Order Information
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                    <Box sx={{ display: 'grid', gap: 1 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" color="textSecondary">Order ID:</Typography>
                        <Typography variant="body2" fontWeight="bold">{detailsModal?.order?.invoiceId || detailsModal?.order?._id}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" color="textSecondary">Date:</Typography>
                        <Typography variant="body2">{moment(detailsModal?.order?.createdAt).format('DD MMM YYYY, hh:mm A')}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body2" color="textSecondary">Status:</Typography>
                        <Chip 
                          icon={getStatusIcon(detailsModal?.order?.status)}
                          label={detailsModal?.order?.status?.replace(/_/g, ' ')} 
                          color={getStatusColor(detailsModal?.order?.status)}
                          size="small"
                        />
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" color="textSecondary">Total Items:</Typography>
                        <Typography variant="body2">{detailsModal?.order?.products?.length || 0}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" color="textSecondary">Total Amount:</Typography>
                        <Typography variant="body1" fontWeight="bold" color="primary">{IndianRupee(detailsModal?.order?.amount)}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" color="textSecondary">Source:</Typography>
                        <Chip label={detailsModal?.order?.source || "App"} size="small" variant="outlined" />
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              {/* Customer Info */}
              <Grid item xs={12} md={6}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle1" fontWeight="bold" gutterBottom color="primary">
                      <LocationOn sx={{ fontSize: 18, mr: 1, verticalAlign: 'text-bottom' }} />
                      Customer & Delivery Info
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                    <Box sx={{ display: 'grid', gap: 1 }}>
                      <Typography variant="body2" fontWeight="bold">
                        {detailsModal?.order?.customerName || detailsModal?.order?.addressId?.email?.split('@')[0] || 'Customer'}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Phone fontSize="small" color="action" />
                        <Typography variant="body2">{detailsModal?.order?.addressId?.phone || '-'}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Email fontSize="small" color="action" />
                        <Typography variant="body2">{detailsModal?.order?.addressId?.email || '-'}</Typography>
                      </Box>
                      <Divider sx={{ my: 1 }} />
                      <Typography variant="body2" fontWeight="bold">Delivery Address:</Typography>
                      <Typography variant="body2">{detailsModal?.order?.addressId?.address || '-'}</Typography>
                      <Typography variant="body2">
                        {detailsModal?.order?.addressId?.city}, {detailsModal?.order?.addressId?.state} - {detailsModal?.order?.addressId?.pincode}
                      </Typography>
                      <Typography variant="body2">{detailsModal?.order?.addressId?.country}</Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              {/* Products */}
              <Grid item xs={12}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle1" fontWeight="bold" gutterBottom color="primary">
                      <ShoppingBag sx={{ fontSize: 18, mr: 1, verticalAlign: 'text-bottom' }} />
                      Products
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                            <TableCell>#</TableCell>
                            <TableCell>Image</TableCell>
                            <TableCell>Product</TableCell>
                            <TableCell align="right">Price</TableCell>
                            <TableCell align="right">Qty</TableCell>
                            <TableCell align="right">Total</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {detailsModal?.order?.products?.map((item, index) => (
                            <TableRow key={index}>
                              <TableCell>{index + 1}</TableCell>
                              <TableCell>
                                <Avatar 
                                  src={api_urls + item?.productId?.bannerImages?.[0]} 
                                  variant="rounded"
                                  sx={{ width: 50, height: 50 }}
                                />
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" fontWeight="bold">
                                  {item?.productId?.productName || 'Product'}
                                </Typography>
                              </TableCell>
                              <TableCell align="right">{IndianRupee(item?.price)}</TableCell>
                              <TableCell align="right">{item?.quantity}</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                                {IndianRupee(item?.price * item?.quantity)}
                              </TableCell>
                            </TableRow>
                          ))}
                          <TableRow>
                          <TableCell colSpan={5} align="right">Product Total:</TableCell>
                          <TableCell align="right">
                            {IndianRupee(detailsModal?.order?.amount)}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell colSpan={5} align="right">Shipping Charges:</TableCell>
                          <TableCell align="right">
                            {detailsModal?.order?.shippingCharges > 0 || (detailsModal?.order?.amount < 1000) 
                              ? IndianRupee(detailsModal?.order?.shippingCharges || 90) 
                              : 'FREE'}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell colSpan={5} align="right" sx={{ fontWeight: 'bold' }}>Grand Total:</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '1.1rem', color: '#1976d2' }}>
                            {IndianRupee(detailsModal?.order?.totalPaid || (detailsModal?.order?.amount + (detailsModal?.order?.shippingCharges || (detailsModal?.order?.amount < 1000 ? 90 : 0))))}
                          </TableCell>
                        </TableRow>
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
          <Button 
            variant="outlined" 
            startIcon={<Print />}
            onClick={() => window.print()}
          >
            Print
          </Button>
          <Button 
            variant="contained" 
            onClick={() => setDetailsModal({ isOpen: false, order: null })}
          >
            Close
          </Button>
        </Box>
      </Dialog>

      {/* Product Modal (keeping original for backward compatibility) */}
      <Dialog open={productModal?.isOpen} PaperProps={{ sx: { maxWidth: { xs: '90vw', sm: '50vw' }, minWidth: { xs: '90vw', sm: '50vw' } } }}>
        <DialogContent>
          <Grid container sx={{ alignItems: "center" }} spacing={3}>
            <Grid item lg={12} md={12} sm={12} xs={12} style={{ fontSize: "22px", fontWeight: "500", color: Color.black }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: "10px" }}>
                <div>Products</div>
                <div onClick={() => setProductModal({ isOpen: false, productData: [] })} style={{ cursor: "pointer" }}><CrossSvg /></div>
              </div>
            </Grid>

            {/* Iterate over each product in productData */}
            {productModal?.productData?.map((value, index) => (
              <Grid container item xs={12} spacing={2} key={index} sx={{ marginBottom: '20px' }}>
                <Grid item xs={12} md={4}>
                  <Avatar src={api_urls + value?.productId?.bannerImages[0]} alt={value?.productId?.productName} style={{ width: '100%', height: '100%', borderRadius: '8px' }} />
                </Grid>

                <Grid item xs={12} md={8}>
                  <Typography variant="h6" fontWeight="bold">{value?.productId?.productName}</Typography>
                  <Typography variant="body2" sx={{ marginBottom: '10px' }}>{value?.productId?.description?.substring(0, 100)}...</Typography>
                  <Typography variant="body2" fontWeight="bold" color={Color.primary}>Price: {IndianRupee(value?.price)}</Typography>
                  <Typography variant="body2">Quantity: {value?.quantity}</Typography>
                </Grid>

                {index !== productModal?.productData?.length - 1 && (<Grid item xs={12}><Divider /></Grid>)}
              </Grid>
            ))}
          </Grid>
        </DialogContent>
      </Dialog>
    </Box>
  );
}

export default OrderHistory;
